<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import Switch from "@/ui/components/switch/switch.vue";
import FilterFileChip from "@/modules/explorer/components/SecondaryDrawer/FilterFileChip.vue";
import ReverseDimensionSearch from "@/modules/explorer/components/SecondaryDrawer/ReverseDimensionSearch.vue";
import useDialog from "@/ui/composables/useDialog";
import { useApiClient } from "@/api/client";
import { ApiError, Result } from "@/api/base";
import { loadingWrapperLocal } from "@/api/helpers/loadingWrapper";
import {
	filterSupportsQuickFilter,
	hierarchyLoadErrorMessage,
} from "@/modules/explorer/components/FilterBar/filterUtils";
import { t } from "@/plugins/i18n";
import { describeFilter } from "./describeFilter";
import { messageTemplate } from "./messageTemplate";
import { useFilterDraft } from "./useFilterDraft";
import { availableDomains, scopeToDomains } from "./domainScope";
import { useHierarchyTree } from "./useHierarchyTree";
import DimensionRail from "./DimensionRail.vue";
import HierarchyTree from "./HierarchyTree.vue";
import FilterList from "./FilterList.vue";
import PeriodValues from "./PeriodValues.vue";
import PolarityToggle from "./PolarityToggle.vue";
import SegmentValues from "./SegmentValues.vue";
import { soleSelectedKey, type ValueRow } from "./valueRow";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import DimensionDO = App.Data.DimensionDO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import DocumentDO = App.Data.Explorer.DocumentDO;

const props = withDefaults(
	defineProps<{
		availableDimensions: DimensionDO[];
		activeFilters: ActiveFilterDTO[];
		activePins?: string[];
		selectedFilter?: ActiveFilterDTO | null;
		document?: DocumentDO | null;
		fixedDomains?: string[];
		apiClient: "explorer" | "segments" | "dashboard";
		currentSegmentId?: number | null;
		hasInvalidDataSource?: (dimension: DocumentDimensionDTO) => boolean;
	}>(),
	{
		activePins: () => [],
		selectedFilter: null,
		document: null,
		fixedDomains: () => [],
		currentSegmentId: null,
		hasInvalidDataSource: () => false,
	}
);

const emit = defineEmits<{
	(event: "apply", filter: ActiveFilterDTO, mode: "create" | "edit"): void;
	(event: "close"): void;
}>();

const open = defineModel<boolean>({ default: false });

const dialog = useDialog();
const draft = useFilterDraft();

const openedOnExistingFilter = ref(false);
const isLoadingValues = ref(false);
const isLoadingHierarchy = ref(false);
const availableValues = ref<FilterValueDTO[]>([]);
const hierarchyRoot = ref<QuickFilterNodeDTO | null>(null);
// Why the tree came back empty, when the reason is a failed load rather than a childless node.
const hierarchyError = ref<string | null>(null);
const isValueSearchOpen = ref(false);

const tree = useHierarchyTree(hierarchyRoot);

/**
 * Where the caller pins the menu to the domains the view on screen covers, the
 * list follows them. A surface that pins nothing — a dashboard spans many
 * verticals, a segment has none — lets the user narrow the list to a single
 * domain instead, or to none of them, which means all of them.
 */
const selectedDomain = defineModel<string | null>("domain", { default: null });

const isDomainSelectable = computed(() => props.fixedDomains.length === 0);

const domainOptions = computed(() =>
	availableDomains(props.availableDimensions)
);

// Whatever the scope, the dimensions already in play stay on offer: an active
// filter or a pinned chip must remain openable even from another domain.
const dimensionsInPlay = computed(() => [
	...props.activeFilters.map((filter) => filter.dimension.name_clean),
	...props.activePins,
	...(props.selectedFilter ? [props.selectedFilter.dimension.name_clean] : []),
]);

// Dimensions land after the dialog is mounted, so the first domain can only be
// taken once the options exist. Only ever fills an empty pick: a domain the
// caller seeded, or one the user chose (including clearing to all domains),
// stands.
watch(
	domainOptions,
	(options) => {
		if (
			isDomainSelectable.value &&
			selectedDomain.value === null &&
			options.length > 0
		) {
			selectedDomain.value = options[0];
		}
	},
	{ immediate: true }
);

const scopedDomains = computed(() => {
	if (!isDomainSelectable.value) {
		return props.fixedDomains;
	}

	return selectedDomain.value === null ? [] : [selectedDomain.value];
});

const railDimensions = computed(() =>
	scopeToDomains(
		props.availableDimensions,
		scopedDomains.value,
		dimensionsInPlay.value
	)
);

const dimension = computed(() => draft.dimension.value);
const dimensionType = computed(() => dimension.value?.type ?? null);
const isHierarchy = computed(
	() =>
		Boolean(dimension.value?.hierarchy_definition_id) &&
		dimensionType.value === "hierarchy"
);
const isException = computed(() => dimensionType.value === "exception");
const isPeriod = computed(() => dimensionType.value === "datetime");
const isSegment = computed(() => dimensionType.value === "segment");
const hasSpecifications = computed(
	() =>
		dimensionType.value === "hierarchy" || dimensionType.value === "bucketed"
);
const isEditing = computed(() => draft.mode.value === "edit");

const adoptedExistingFilter = computed(
	() => isEditing.value && !openedOnExistingFilter.value
);

const dialogTitle = computed(() => {
	if (!isEditing.value) {
		return t("filterDialog.title.create");
	}

	if (!openedOnExistingFilter.value || !dimension.value) {
		return t("filterDialog.title.edit");
	}

	return t("filterDialog.title.editNamed", {
		dimension: t(`properties.${dimension.value.name_clean}.title`),
	});
});

let loadSequence = 0;
let isSelectingDimension = false;

const loadHierarchy = async (token: number) => {
	const hierarchyId = dimension.value?.hierarchy_definition_id;

	if (!hierarchyId) {
		hierarchyRoot.value = null;
		return;
	}

	const request = useApiClient(props.apiClient).getHierarchy(
		props.document,
		hierarchyId,
		[],
		false,
		null
	) as unknown as Promise<Result<QuickFilterNodeDTO[], ApiError>>;

	const response = await loadingWrapperLocal(request, isLoadingHierarchy);

	// A stale token means the user has already moved to another dimension — neither the tree nor
	// the message belongs to what's on screen any more.
	if (token !== loadSequence) {
		return;
	}

	if (response.isErr()) {
		hierarchyError.value = hierarchyLoadErrorMessage(response.error);
		return;
	}

	// Not ok and not an error means canceled: a newer request is already in flight, so leave the
	// message alone rather than reporting a failure that didn't happen.
	if (!response.isOk()) {
		return;
	}

	hierarchyRoot.value = response.value?.[0] ?? null;
};

const loadFlatValues = async (token: number) => {
	// Period editors build their own options; segments have their own endpoint.
	if (
		!dimension.value ||
		isException.value ||
		isPeriod.value ||
		isSegment.value
	) {
		availableValues.value = [];
		return;
	}

	const response = await loadingWrapperLocal(
		useApiClient(props.apiClient).getValuesForDimensionAndSpecification(
			dimension.value.id,
			dimension.value.dimension_bucket_id ?? null
		),
		isLoadingValues
	);

	if (token !== loadSequence || !response.isOk()) {
		return;
	}

	availableValues.value = [...response.value];

	if (dimension.value.type === "bucketed") {
		availableValues.value.push({
			id: 0,
			label: "Unknown",
			level: null,
			model: null,
			selection_depth: null,
		} as FilterValueDTO);
	}
};

const loadForDimension = async () => {
	const token = ++loadSequence;

	availableValues.value = [];
	hierarchyRoot.value = null;
	hierarchyError.value = null;

	if (dimensionType.value === "hierarchy") {
		await loadHierarchy(token);
		return;
	}

	await loadFlatValues(token);
};

const selectDimension = async (
	selected: DimensionDO,
	specification?: number
) => {
	const isSameDimension =
		selected.name_clean === dimension.value?.name_clean &&
		(specification === undefined ||
			specification === draft.specification.value);

	if (isSameDimension) {
		return;
	}

	if (draft.isDirty(hierarchyValue.value)) {
		const confirm = await dialog.confirm({
			title: t("filterDialog.discard.title"),
			message: t("filterDialog.discard.message"),
			confirmButtonText: t("filterDialog.discard.confirm"),
			cancelButtonText: t("filterDialog.discard.cancel"),
		});

		if (!confirm) {
			return;
		}
	}

	isSelectingDimension = true;

	// Clears everything the previously selected dimension left behind: values,
	// polarity, file, sorting and the edit mode of an adopted filter.
	draft.reset();
	draft.dimension.value = { ...selected } as DocumentDimensionDTO;
	draft.applyDefaultSpecification();

	const existing = props.activeFilters.find(
		(filter) => filter.dimension.name_clean === selected.name_clean
	);

	if (existing) {
		draft.loadFrom(existing, props.availableDimensions);
	} else if (selected.type === "hierarchy") {
		draft.isQuickFilter.value = true;
	}

	if (specification) {
		draft.specification.value = specification;
	}

	await loadForDimension();

	isSelectingDimension = false;

	if (existing?.dimension.type === "hierarchy") {
		tree.loadFromValue(existing.values?.[0]);
	}

	draft.markClean(isHierarchy.value ? tree.toFilterValue() : null);
};

watch(
	() => draft.specification.value,
	async (value, previous) => {
		if (isSelectingDimension || value === previous || !dimension.value) {
			return;
		}

		draft.values.value = [];
		await loadForDimension();
	}
);

// A saved filter can point at a specification the user can no longer pick: a hierarchy made
// private or deleted never comes back in `availableSpecifications`. VSelect falls back to
// rendering the raw model value when it can't match an item, so the field showed a bare id.
const missingSpecificationId = computed(() => {
	const selected = draft.specification.value;

	if (!hasSpecifications.value || selected === null) {
		return null;
	}

	const isAvailable = draft.availableSpecifications.value.some(
		(candidate: { id: number | null }) => candidate.id === selected
	);

	return isAvailable ? null : selected;
});

// Stands the missing one in as a labelled, disabled entry. Clearing the value instead would
// silently rewrite the document to a different hierarchy the moment the user hits apply, so the
// filter keeps pointing where it always did — the picker just says why it can't be chosen.
const specificationItems = computed(() => {
	if (missingSpecificationId.value === null) {
		return draft.availableSpecifications.value;
	}

	return [
		...draft.availableSpecifications.value,
		{
			id: missingSpecificationId.value,
			name:
				(isHierarchy.value ? hierarchyError.value : null) ??
				t("filterDialog.hierarchy.definitionUnavailable"),
		},
	];
});

const valueKey = (value: FilterValueDTO) => String(value.id ?? value.label);

const valueRows = computed<ValueRow[]>(() =>
	availableValues.value.map((value) => ({
		key: valueKey(value),
		label: value.label ?? "",
	}))
);

const selectedKeys = computed(() => new Set(draft.values.value.map(valueKey)));

const toggleValue = (row: ValueRow) => {
	if (selectedKeys.value.has(row.key)) {
		draft.values.value = draft.values.value.filter(
			(value) => valueKey(value) !== row.key
		);
		return;
	}

	const found = availableValues.value.find(
		(value) => valueKey(value) === row.key
	);

	if (found) {
		draft.values.value = [...draft.values.value, found];
	}
};

const selectValues = (rows: ValueRow[]) => {
	const wanted = new Set(rows.map((row) => row.key));
	const additions = availableValues.value.filter(
		(value) =>
			wanted.has(valueKey(value)) && !selectedKeys.value.has(valueKey(value))
	);

	draft.values.value = [...draft.values.value, ...additions];
};

const deselectValues = (rows: ValueRow[]) => {
	const unwanted = new Set(rows.map((row) => row.key));

	draft.values.value = draft.values.value.filter(
		(value) => !unwanted.has(valueKey(value))
	);
};

const onlyValue = (row: ValueRow, scope: ValueRow[]) => {
	const sole = soleSelectedKey(scope, (candidate) =>
		selectedKeys.value.has(candidate.key)
	);

	if (sole === row.key) {
		selectValues(scope);
		return;
	}

	const onScreen = new Set(scope.map((other) => other.key));
	const kept = draft.values.value.filter(
		(value) => !onScreen.has(valueKey(value))
	);
	const found = availableValues.value.find(
		(value) => valueKey(value) === row.key
	);

	draft.values.value = found ? [...kept, found] : kept;
};

const hierarchyValue = computed(() =>
	isHierarchy.value ? tree.toFilterValue() : null
);

const summary = computed(() =>
	describeFilter(
		dimension.value ? draft.toFilter(hierarchyValue.value) : null,
		messageTemplate,
		{
			depthYield: isHierarchy.value ? tree.depthYield.value : null,
			totalValues: availableValues.value.length || null,
		}
	)
);

const canSubmit = computed(() => {
	if (!dimension.value) {
		return false;
	}

	if (isHierarchy.value) {
		return Boolean(tree.scope.value);
	}

	if (isException.value) {
		return draft.fileText.value.length > 0 || draft.fileName.value.length > 0;
	}

	return draft.values.value.length > 0;
});

const showQuickFilterToggle = computed(
	() =>
		Boolean(dimension.value) &&
		filterSupportsQuickFilter({
			dimension: dimension.value,
		} as ActiveFilterDTO)
);

const apply = () => {
	emit("apply", draft.toFilter(hierarchyValue.value), draft.mode.value);
	draft.markClean(hierarchyValue.value);
	open.value = false;
	emit("close");
};

const requestClose = async () => {
	if (!draft.isDirty(hierarchyValue.value)) {
		open.value = false;
		emit("close");
		return;
	}

	const confirmed = await dialog.confirm({
		title: t("filterDialog.discard.title"),
		message: t("filterDialog.discard.message"),
		confirmButtonText: t("filterDialog.discard.confirm"),
		cancelButtonText: t("filterDialog.discard.cancel"),
	});

	if (confirmed) {
		open.value = false;
		emit("close");
	}
};

const visible = computed({
	get: () => open.value,
	set: (value: boolean) => {
		if (value) {
			open.value = true;
			return;
		}

		requestClose();
	},
});

watch(
	open,
	async (isOpen) => {
		if (!isOpen) {
			return;
		}

		draft.reset();

		const selected = props.selectedFilter;

		openedOnExistingFilter.value = Boolean(selected);

		if (!selected) {
			draft.markClean();
			return;
		}

		draft.loadFrom(selected, props.availableDimensions);

		// A pinned chip hands us a dimension that has no filter yet; staying in
		// edit mode would make the store replace nothing when we apply.
		const isExistingFilter = props.activeFilters.some(
			(filter) => filter.dimension.name_clean === selected.dimension.name_clean
		);

		if (!isExistingFilter) {
			draft.mode.value = "create";
		}

		await loadForDimension();

		if (selected.dimension.type === "hierarchy") {
			tree.loadFromValue(selected.values?.[0]);
		}

		draft.markClean(isHierarchy.value ? tree.toFilterValue() : null);
	},
	{ immediate: true }
);

watch(
	() => props.availableDimensions,
	(available) => {
		const current = draft.dimension.value;

		if (
			!current ||
			available.length === 0 ||
			draft.availableSpecifications.value.length > 0
		) {
			return;
		}

		const richer = available.find((candidate) => candidate.id === current.id);

		if (!richer) {
			return;
		}

		const wasClean = !draft.isDirty(hierarchyValue.value);

		draft.dimension.value = { ...richer, ...current } as DocumentDimensionDTO;

		if (draft.specification.value === null) {
			draft.applyDefaultSpecification();
		}

		// Arriving data is not an edit the user made, so it must not arm the discard prompt.
		if (wasClean) {
			draft.markClean(isHierarchy.value ? tree.toFilterValue() : null);
		}
	}
);
</script>
<template>
	<reverse-dimension-search
		v-model="isValueSearchOpen"
		:results-limit="100"
		@dimension-selected="
			(selected, specification) => selectDimension(selected, specification)
		"
	/>
	<modal
		v-model:visible="visible"
		:title="dialogTitle"
		icon="fa-regular fa-filter"
		:width="openedOnExistingFilter ? '720' : '1040'"
		no-body-padding
		:persistent="false"
	>
		<div class="body" :class="{ 'body--solo': openedOnExistingFilter }">
			<dimension-rail
				v-if="!openedOnExistingFilter"
				v-model:domain="selectedDomain"
				class="rail-pane"
				:dimensions="railDimensions"
				:domain-options="domainOptions"
				:is-domain-selectable="isDomainSelectable"
				:active-pins="activePins"
				:selected-name-clean="dimension?.name_clean ?? null"
				:allow-value-search="true"
				@select="selectDimension"
				@search-by-value="isValueSearchOpen = true"
			/>
			<div class="config-pane pa-4">
				<div v-if="!dimension" class="empty-state text-body-2">
					{{ t("filterDialog.summary.empty") }}
				</div>
				<template v-else>
					<div class="config-stack d-flex flex-column ga-5">
						<v-alert
							v-if="adoptedExistingFilter"
							density="compact"
							class="text-body-2 flex-0-0"
						>
							{{
								t("filterDialog.notice.adopted", {
									dimension: t(`properties.${dimension.name_clean}.title`),
								})
							}}
						</v-alert>
						<v-alert
							v-if="hasInvalidDataSource(dimension)"
							type="warning"
							density="compact"
							class="text-caption flex-grow-0"
						>
							{{ t("filterDialog.warning.invalidDataSource") }}
						</v-alert>
						<section
							v-if="
								hasSpecifications &&
								draft.availableSpecifications.value.length > 0
							"
							class="zone"
						>
							<h6 class="zone-label">
								{{ t("filterDialog.hierarchy.definition") }}
							</h6>
							<v-select
								v-model="draft.specification.value"
								:items="specificationItems"
								:item-props="
									(item) => ({
										disabled: item.id === missingSpecificationId,
									})
								"
								:error="missingSpecificationId !== null"
								item-title="name"
								item-value="id"
								hide-details
							/>
						</section>
						<hierarchy-tree
							v-if="isHierarchy"
							:tree="tree"
							:is-loading="isLoadingHierarchy"
							:error-message="hierarchyError"
						/>
						<segment-values
							v-else-if="isSegment"
							v-model:values="draft.values.value"
							v-model:included="draft.valuesAreIncluded.value"
							v-model:cohort-months="draft.cohortMonths.value"
							:dimension="dimension"
							:api-client="apiClient"
							:current-segment-id="currentSegmentId"
						/>
						<period-values
							v-else-if="isPeriod"
							v-model:values="draft.values.value"
							v-model:included="draft.valuesAreIncluded.value"
							:dimension="dimension"
						/>
						<section v-else-if="isException" class="zone">
							<header class="zone-header">
								<h6 class="zone-label">
									{{ t("filterDialog.exception.heading") }}
								</h6>
								<polarity-toggle v-model="draft.valuesAreIncluded.value" />
							</header>
							<p class="text-body-2 muted mb-0">
								{{
									draft.isUploadedFile.value
										? t("filterDialog.exception.locked")
										: t("filterDialog.exception.hint")
								}}
							</p>
							<v-textarea
								v-if="!draft.isUploadedFile.value"
								v-model="draft.fileText.value"
								hide-details
								rows="6"
							/>
							<filter-file-chip
								v-show="
									draft.isUploadedFile.value ||
									draft.fileText.value.length === 0
								"
								:locked="draft.isUploadedFile.value"
								:file-name="draft.fileName.value"
								:file-uuid="draft.fileUuid.value"
								@update="draft.setFile"
							/>
						</section>
						<section v-else class="zone">
							<header class="zone-header">
								<h6 class="zone-label">
									{{ t("filterDialog.values.heading") }}
								</h6>
								<polarity-toggle v-model="draft.valuesAreIncluded.value" />
							</header>
							<v-progress-linear
								v-if="isLoadingValues"
								indeterminate
								height="2"
							/>
							<filter-list
								:rows="valueRows"
								:initial-limit="50"
								:is-checked="(row) => selectedKeys.has(row.key)"
								@toggle="toggleValue"
								@only="onlyValue"
								@select-all="selectValues"
								@deselect-all="deselectValues"
							/>
							<p class="text-body-2 muted mb-0">
								{{
									t("filterDialog.values.selectedCount", {
										count: draft.values.value.length,
									})
								}}
							</p>
						</section>

						<section class="workspace pa-2">
							<div v-if="showQuickFilterToggle" class="workspace-row">
								<Switch
									v-model="draft.isQuickFilter.value"
									:label="t('filterDialog.workspace.quickFilter')"
									label-position="right"
									hide-details
									data-testid="quick-filter-switch"
								/>
								<span
									v-tooltip="t('filterDialog.workspace.quickFilterHint')"
									class="hint"
								>
									<v-icon icon="fa-light fa-circle-info" size="14" />
								</span>
							</div>
							<div class="workspace-row">
								<Switch
									v-model="draft.isApplied.value"
									:label="t('filterDialog.workspace.isApplied')"
									label-position="right"
									hide-details
								/>
								<span
									v-tooltip="t('filterDialog.workspace.isAppliedHint')"
									class="hint"
								>
									<v-icon icon="fa-light fa-circle-info" size="14" />
								</span>
							</div>
						</section>
					</div>
				</template>
			</div>
		</div>
		<template #actions>
			<div class="footer">
				<p class="summary text-body-2">
					<span
						v-for="(segment, index) in summary"
						:key="index"
						:class="`seg-${segment.kind}`"
						>{{ segment.text }}</span
					>
				</p>
				<div class="d-flex ga-2">
					<v-btn variant="text" @click="requestClose">
						{{ t("filterDialog.action.cancel") }}
					</v-btn>
					<v-btn
						:disabled="!canSubmit"
						prepend-icon="fa: fa-regular fa-check"
						@click="apply"
					>
						{{
							isEditing
								? t("filterDialog.action.update")
								: t("filterDialog.action.apply")
						}}
					</v-btn>
				</div>
			</div>
		</template>
	</modal>
</template>
<style scoped lang="scss">
.body {
	display: grid;
	grid-template-columns: 17rem 1fr;
	height: 72vh;
	min-height: 0;
}

.body--solo {
	grid-template-columns: 1fr;
}

.rail-pane {
	min-height: 0;
}

.config-pane {
	overflow-y: auto;
	min-height: 0;
}

.empty-state {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: rgb(var(--color-on-background));
}

.zone {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.zone-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
}

.zone-label {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: rgb(var(--color-on-background));
	margin-bottom: 0;
}

.muted {
	color: rgb(var(--color-on-background));
}

.config-stack {
	min-height: 100%;
}

.workspace {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	border: 1px solid rgb(var(--color-secondary));
	border-radius: 4px;
	background: rgb(var(--color-background));
	margin-top: auto;
}

.workspace-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
}

.hint {
	display: inline-flex;
	align-items: center;
	color: rgb(var(--color-on-background));
	cursor: help;
}

.footer {
	display: flex;
	align-items: center;
	gap: 1rem;
	width: 100%;
}

.summary {
	flex: 1 1 auto;
	margin: 0;
	color: rgb(var(--color-on-background));
}

.seg-dimension {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
}

.seg-scope,
.seg-depth,
.seg-value {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
}

.seg-exclusion {
	font-weight: 600;
	color: rgb(var(--color-error));
}

.seg-yield {
	font-variant-numeric: tabular-nums;
}

.seg-prompt {
	font-style: italic;
}
</style>
